import { startTransition, useCallback, useRef, useState } from 'react'
import {
  randomUUID,
  type AgentSubscriber,
  type BaseEvent,
  type Message,
  type ResumeEntry,
} from '@ag-ui/client'
import { fetchWholeConversation } from '../api/conversations'
import { aiServerBaseFor, describeShareError, fetchWholeShared, forkShared } from '../api/sharing'
import { CONVERSATION_RUN_STATE_EVENT, CONVERSATION_UPDATED_EVENT } from '../config/chat.config'
import type {
  ChatController,
  ChatSession,
  ChatState,
  InterruptDraft,
  SharedView,
  UseChatControllerOptions,
} from '../types/chat'
import { createAgent, createThreadId } from '../utils/agent'
import { toAgUiMessages } from '../utils/conversationMessages'
import { summarizeEvent } from '../utils/events'
import {
  buildResumeEntries,
  createInterruptDrafts,
  setAllInterruptDrafts,
} from '../utils/interrupts'
import { isRecord, safeJson } from '../utils/json'
import { findLastUserMessageId } from '../utils/messages'

/**
 * Maps the orchestrator's route slug (AG-UI STATE_SNAPSHOT's `activeAgent`)
 * to a human label for the "Routing to X…" status. "clarify" is deliberately
 * absent — the orchestrator asking a clarifying question isn't "routing"
 * anywhere, so no detail is shown for it and the generic "Thinking…" stands.
 */
const ROUTE_LABELS: Record<string, string> = {
  it_support: 'IT Support',
  order_it: 'Order IT',
  eon_knowledge: 'E.ON Knowledge',
  mygenius: 'MyGenius',
}

function routingDetailFromState(state: unknown): string | undefined {
  if (!isRecord(state) || typeof state.activeAgent !== 'string') return undefined
  const label = ROUTE_LABELS[state.activeAgent]
  return label ? `Routing to ${label}…` : undefined
}

/**
 * Reads as a sentence: "Searching Compass…". Raw MCP tool ids leak internal
 * naming, and duplicate ids arrive namespaced as `${serverId}__${name}`, so the
 * bare name is resolved off the suffix. An unmapped tool keeps its own name.
 */
const TOOL_LABELS: Record<string, string> = {
  'apps-hr-admin-search': 'Searching Apps & HR knowledge',
  'compass-search': 'Searching Compass',
  'confluence-search': 'Searching Confluence',
  'mygenius-search': 'Searching MyGenius',
  'order-it-search': 'Searching the IT order portal',
  'order-it-list-laptops': 'Looking up available laptops',
  'it-snow-search': 'Searching the ServiceNow knowledge base',
  'create-incident': 'Raising a ticket',
  'get-user-incidents': 'Checking your tickets',
  'graph-search-people': 'Looking up people',
  'graph-search-manager': 'Looking up reporting lines',
  'graph-search-direct-reports': 'Looking up direct reports',
}

function toolCallDetail(toolCallName: string): string {
  const bareName = toolCallName.includes('__')
    ? toolCallName.slice(toolCallName.lastIndexOf('__') + 2)
    : toolCallName
  const label = TOOL_LABELS[bareName]

  return label ? `${label}…` : `Using ${toolCallName}…`
}

export function useChatController({
  authenticatedFetch,
  config,
}: UseChatControllerOptions): ChatController {
  const [agentEndpoint, setAgentEndpoint] = useState(config.agentEndpoint)
  const [mcpProxyEndpoint, setMcpProxyEndpoint] = useState(
    config.mcpProxyEndpoint,
  )
  const [session, setSession] = useState<ChatSession>(() => {
    const threadId = createThreadId()
    return {
      agent: createAgent(config.agentEndpoint, threadId, authenticatedFetch),
      threadId,
    }
  })
  const [threadId, setThreadId] = useState(session.threadId)
  const [messages, setMessages] = useState(session.agent.messages)
  const [events, setEvents] = useState<ChatState['events']>([])
  const [tools, setTools] = useState<ChatState['tools']>([])
  const [agentState, setAgentState] = useState<unknown>({})
  const [draft, setDraft] = useState('')
  const [isRunning, setIsRunning] = useState(false)
  const [isLoadingThread, setIsLoadingThread] = useState(false)
  const [activeAssistantRun, setActiveAssistantRun] = useState<
    ChatState['activeAssistantRun']
  >(null)
  const [error, setError] = useState<string | null>(null)
  const [pendingInterrupts, setPendingInterrupts] = useState<
    ChatState['pendingInterrupts']
  >([])
  const [interruptDrafts, setInterruptDrafts] = useState<
    Record<string, InterruptDraft>
  >({})
  const [sharedView, setSharedView] = useState<SharedView | null>(null)
  const [sharedLinkError, setSharedLinkError] = useState<string | null>(null)
  const [isForking, setIsForking] = useState(false)
  // State alone cannot stop two clicks in the same render from forking twice.
  const forkInFlightRef = useRef(false)
  const aiServerBase = aiServerBaseFor(config)
  const activeRunIdRef = useRef<string | null>(null)
  // Counts tool calls started but not yet resulted, for the current run. Once
  // it drops back to 0 after having been above 0, every tool call the model
  // has asked for so far has answered — a safe point to call the run
  // 'summarizing' rather than still 'calling-tools'. Reset per run, not per
  // tool call, since a run can involve several tool calls in sequence.
  const pendingToolCallCountRef = useRef(0)
  // Guards against a stale `loadThread` call overwriting a newer one — e.g. a
  // user clicking a second Recent Chats row before the first thread finishes
  // paging in. Only the most recent request is allowed to commit its result.
  const loadThreadRequestIdRef = useRef(0)
  const agent = session.agent

  const finishAssistantRun = useCallback((runId: string) => {
    setActiveAssistantRun((current) =>
      current?.runId === runId ? null : current,
    )
  }, [])

  const setRunningState = useCallback((running: boolean) => {
    setIsRunning(running)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent(CONVERSATION_RUN_STATE_EVENT, { detail: { isRunning: running } }),
      )
    }
  }, [])

  const syncMessages = useCallback(
    (nextMessages: Parameters<
      NonNullable<AgentSubscriber['onMessagesChanged']>
    >[0]['messages']) => {
      startTransition(() => {
        setMessages(nextMessages.map((message) => message as Message))
      })
    },
    [],
  )

  const addEvent = useCallback(
    (event: BaseEvent) => {
      setEvents((current) =>
        [
          {
            id: `${Date.now()}_${Math.random()}`,
            type: event.type,
            summary: summarizeEvent(event),
            time: new Date().toLocaleTimeString(),
          },
          ...current,
        ].slice(0, config.maxEventRecords),
      )
    },
    [config.maxEventRecords],
  )

  const buildSubscriber = useCallback(
    (): AgentSubscriber => {
      const subscriber: AgentSubscriber = {
        onMessagesChanged: ({ input, messages: nextMessages }) => {
          if (input?.runId && input.runId !== activeRunIdRef.current) return
          syncMessages(nextMessages)
        },
        onRunStartedEvent: ({ event, messages: runMessages }) => {
          if (event.runId !== activeRunIdRef.current) return
          pendingToolCallCountRef.current = 0
          const { runId } = event
          setActiveAssistantRun({
            runId,
            userMessageId: findLastUserMessageId(runMessages),
            status: 'thinking',
          })
        },
        // Unconditional (not behind config.debugEnabled): STATE_SNAPSHOT carries
        // the orchestrator's routing decision (`activeAgent`), which is exactly
        // the "which specialist is now handling this" signal real users should
        // see too, not just a debug trace. Only updates `detail` while still in
        // the 'thinking' phase — once a tool call starts, its name is a more
        // specific, more current status than the route it was sent to.
        onStateChanged: ({ state, input }) => {
          if (input?.runId !== activeRunIdRef.current) return
          const detail = routingDetailFromState(state)
          if (!detail) return

          setActiveAssistantRun((current) =>
            current && current.runId === input?.runId && current.status === 'thinking'
              ? { ...current, detail }
              : current,
          )
        },
        onToolCallStartEvent: ({ event, input, messages: runMessages }) => {
          if (input.runId !== activeRunIdRef.current) return
          pendingToolCallCountRef.current += 1
          setActiveAssistantRun((current) => ({
            runId: input.runId,
            userMessageId:
              current?.runId === input.runId
                ? current.userMessageId
                : findLastUserMessageId(runMessages),
            status: 'calling-tools',
            detail: toolCallDetail(event.toolCallName),
          }))

          if (config.debugEnabled) {
            setTools((current) => [
              {
                id: event.toolCallId,
                name: event.toolCallName,
                args: '',
                status: 'running',
              },
              ...current.filter((tool) => tool.id !== event.toolCallId),
            ])
          }
        },
        // Unconditional (not behind config.debugEnabled): once every tool call
        // the model has asked for so far has answered, the run is no longer
        // "calling tools" — it is composing its reply from what came back.
        // Real users benefit from that distinction as much as from knowing a
        // tool ran at all, so this is not gated behind the debug trace either.
        onToolCallResultEvent: ({ input }) => {
          if (input.runId !== activeRunIdRef.current) return
          pendingToolCallCountRef.current = Math.max(
            0,
            pendingToolCallCountRef.current - 1,
          )
          if (pendingToolCallCountRef.current > 0) return

          setActiveAssistantRun((current) =>
            current && current.runId === input.runId
              ? { runId: current.runId, userMessageId: current.userMessageId, status: 'summarizing' }
              : current,
          )
        },
        onRunFinishedEvent: (params) => {
          if (params.event.runId !== activeRunIdRef.current) return
          setMessages(params.messages.map((message) => message as Message))
          finishAssistantRun(params.event.runId)

          // The server may have just persisted a brand-new thread (or added
          // to an existing one) — let the Recent Chats sidebar know it's
          // worth refreshing, without either side needing direct state
          // access to the other (it lives outside this provider).
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent(CONVERSATION_UPDATED_EVENT))
          }

          if (params.outcome === 'interrupt') {
            setPendingInterrupts(params.interrupts)
            setInterruptDrafts(createInterruptDrafts(params.interrupts))
            return
          }

          setPendingInterrupts([])
          setInterruptDrafts({})
        },
        onRunErrorEvent: ({ event, input, messages: runMessages }) => {
          if (input.runId !== activeRunIdRef.current) return
          setMessages(runMessages.map((message) => message as Message))
          finishAssistantRun(input.runId)
          setError(event.message)
        },
      }

      if (!config.debugEnabled) return subscriber

      return {
        ...subscriber,
        onEvent: ({ event }) => {
          addEvent(event)
        },
        onStateChanged: (params) => {
          subscriber.onStateChanged?.(params)
          setAgentState(params.state)
        },
        onToolCallArgsEvent: ({ event, toolCallBuffer, toolCallName }) => {
          setTools((current) =>
            current.map((tool) =>
              tool.id === event.toolCallId
                ? { ...tool, name: toolCallName, args: toolCallBuffer }
                : tool,
            ),
          )
        },
        onToolCallEndEvent: ({ event, toolCallArgs, toolCallName }) => {
          setTools((current) =>
            current.map((tool) =>
              tool.id === event.toolCallId
                ? {
                    ...tool,
                    name: toolCallName,
                    args: safeJson(toolCallArgs),
                    status: 'ready',
                  }
                : tool,
            ),
          )
        },
        onToolCallResultEvent: (params) => {
          subscriber.onToolCallResultEvent?.(params)
          const { event } = params
          setTools((current) =>
            current.map((tool) =>
              tool.id === event.toolCallId
                ? { ...tool, result: event.content, status: 'result' }
                : tool,
            ),
          )
        },
      }
    },
    [addEvent, config.debugEnabled, finishAssistantRun, syncMessages],
  )

  const runAgent = useCallback(
    // `targetAgent` exists for the first reply to a shared chat: the fork has
    // just built a new agent that is not yet `session.agent` in this render.
    async (resume?: ResumeEntry[], targetAgent = agent) => {
      const runner = targetAgent
      const runId = randomUUID()
      activeRunIdRef.current = runId
      setError(null)
      // Optimistic, rather than null until RUN_STARTED: the server does auth,
      // persistence and MCP discovery before opening the stream, and with no
      // active run buildChatDisplayItems emits no assistant turn at all — so
      // the UI would show nothing for seconds after the question is sent.
      setActiveAssistantRun({
        runId,
        userMessageId: findLastUserMessageId(runner.messages),
        status: 'thinking',
      })
      setRunningState(true)

      try {
        await runner.runAgent({ resume, runId }, buildSubscriber())
        if (activeRunIdRef.current === runId) {
          setMessages([...runner.messages])
          if (config.debugEnabled) setAgentState(runner.state)
        }
      } catch (runError) {
        if (activeRunIdRef.current === runId) {
          setError(
            runError instanceof Error ? runError.message : String(runError),
          )
        }
      } finally {
        if (activeRunIdRef.current === runId) {
          activeRunIdRef.current = null
          finishAssistantRun(runId)
          setRunningState(false)
        }
      }
    },
    [agent, buildSubscriber, config.debugEnabled, finishAssistantRun, setRunningState],
  )

  /**
   * Sends one user message. In a shared chat the first message first forks it
   * into a thread the viewer owns (same message ids), then carries on there as
   * an ordinary turn. If the fork fails nothing is sent and the draft is kept.
   */
  const sendUserMessage = useCallback(
    async (rawContent: string) => {
      const content = rawContent.trim()
      if (!content || isRunning || isForking || pendingInterrupts.length > 0) return

      let target = agent

      if (sharedView) {
        if (sharedView.ownThreadId || forkInFlightRef.current) return

        forkInFlightRef.current = true
        setIsForking(true)
        setError(null)
        let forkedThreadId: string
        try {
          forkedThreadId = (await forkShared(authenticatedFetch, aiServerBase, sharedView.token))
            .threadId
        } catch (forkError) {
          setError(describeShareError(forkError))
          return
        } finally {
          forkInFlightRef.current = false
          setIsForking(false)
        }

        target = createAgent(agentEndpoint, forkedThreadId, authenticatedFetch, agent.messages)
        setSession({ agent: target, threadId: forkedThreadId })
        setThreadId(forkedThreadId)
        setSharedView(null)
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent(CONVERSATION_UPDATED_EVENT))
        }
      }

      target.addMessage({
        id: randomUUID(),
        role: 'user',
        content,
      })
      setDraft('')
      setMessages([...target.messages])
      await runAgent(undefined, target)
    },
    [
      agent,
      agentEndpoint,
      aiServerBase,
      authenticatedFetch,
      isForking,
      isRunning,
      pendingInterrupts.length,
      runAgent,
      sharedView,
    ],
  )

  const submitDraft = useCallback(async () => {
    await sendUserMessage(draft)
  }, [draft, sendUserMessage])

  const submitQuestion = useCallback(
    async (question: string) => {
      await sendUserMessage(question)
    },
    [sendUserMessage],
  )

  const resetSession = useCallback(
    (nextThreadId = createThreadId()) => {
      // Invalidates any in-flight `loadThread` so its eventual resolution
      // cannot overwrite the session this call is about to create.
      loadThreadRequestIdRef.current += 1
      agent.abortRun()
      activeRunIdRef.current = null
      setSession({
        agent: createAgent(
          agentEndpoint,
          nextThreadId,
          authenticatedFetch,
        ),
        threadId: nextThreadId,
      })
      setThreadId(nextThreadId)
      setMessages([])
      setEvents([])
      setTools([])
      setAgentState({})
      setDraft('')
      setError(null)
      setActiveAssistantRun(null)
      setIsRunning(false)
      setIsLoadingThread(false)
      setPendingInterrupts([])
      setInterruptDrafts({})
      setSharedView(null)
      setSharedLinkError(null)
    },
    [agent, agentEndpoint, authenticatedFetch],
  )

  const applyConnectionSettings = useCallback(() => {
    resetSession(threadId)
  }, [resetSession, threadId])

  const startNewThread = useCallback(() => {
    resetSession()
  }, [resetSession])

  /**
   * Reopens a persisted thread: pages `GET /threads/:threadId` all the way
   * back to page 1, bridges the full history to AG-UI messages, and rebuilds
   * the agent seeded with them — mirroring `resetSession`, but seeded rather
   * than empty. Deliberately does not call `resetSession`/`setThreadId`
   * alone: `actions.setThreadId` only updates display state, it does not
   * rebuild the agent, so continuing the conversation afterwards would send
   * an incomplete history and get `409 MESSAGE_ID_CONFLICT` on the next turn.
   */
  const loadThread = useCallback(
    async (nextThreadId: string) => {
      const requestId = ++loadThreadRequestIdRef.current
      agent.abortRun()
      activeRunIdRef.current = null
      setIsLoadingThread(true)
      setError(null)

      let initialMessages: Message[]
      try {
        const stored = await fetchWholeConversation(
          authenticatedFetch,
          config.conversationsEndpoint,
          nextThreadId,
        )
        initialMessages = toAgUiMessages(stored.messages, stored.activities)
      } catch (loadError) {
        if (loadThreadRequestIdRef.current === requestId) {
          setIsLoadingThread(false)
          setError(
            loadError instanceof Error ? loadError.message : String(loadError),
          )
        }
        return
      }

      // A newer `loadThread` call has since started — let it win.
      if (loadThreadRequestIdRef.current !== requestId) return

      const nextAgent = createAgent(
        agentEndpoint,
        nextThreadId,
        authenticatedFetch,
        initialMessages,
      )
      setSession({ agent: nextAgent, threadId: nextThreadId })
      setThreadId(nextThreadId)
      setMessages([...nextAgent.messages])
      setEvents([])
      setTools([])
      setAgentState({})
      setDraft('')
      setActiveAssistantRun(null)
      setIsRunning(false)
      setPendingInterrupts([])
      setInterruptDrafts({})
      setSharedView(null)
      setSharedLinkError(null)
      setIsLoadingThread(false)
    },
    [agent, agentEndpoint, authenticatedFetch, config.conversationsEndpoint],
  )

  /**
   * Opens a chat someone shared, read-only. Mirrors `loadThread`, including its
   * stale-request guard, but the session runs on a throwaway thread id: the
   * viewer owns nothing yet. The first message they send forks the chat (see
   * `sendUserMessage`).
   */
  const openShared = useCallback(
    async (token: string) => {
      const requestId = ++loadThreadRequestIdRef.current
      agent.abortRun()
      activeRunIdRef.current = null
      setIsLoadingThread(true)
      setError(null)
      setSharedLinkError(null)

      let initialMessages: Message[]
      let view: SharedView
      try {
        const shared = await fetchWholeShared(authenticatedFetch, aiServerBase, token)
        initialMessages = toAgUiMessages(shared.messages, shared.activities)
        view = {
          token,
          title: shared.share.title,
          expiresAt: shared.share.expiresAt,
          ...(shared.ownThreadId === undefined ? {} : { ownThreadId: shared.ownThreadId }),
        }
      } catch (openError) {
        if (loadThreadRequestIdRef.current === requestId) {
          // Never leave a previous chat on screen behind a "link unavailable" error.
          const blankThreadId = createThreadId()
          setSession({
            agent: createAgent(agentEndpoint, blankThreadId, authenticatedFetch),
            threadId: blankThreadId,
          })
          setThreadId(blankThreadId)
          setMessages([])
          setSharedView(null)
          setIsLoadingThread(false)
          setSharedLinkError(describeShareError(openError))
        }
        return
      }

      if (loadThreadRequestIdRef.current !== requestId) return

      const viewThreadId = createThreadId()
      const nextAgent = createAgent(
        agentEndpoint,
        viewThreadId,
        authenticatedFetch,
        initialMessages,
      )
      setSession({ agent: nextAgent, threadId: viewThreadId })
      setThreadId(viewThreadId)
      setMessages([...nextAgent.messages])
      setEvents([])
      setTools([])
      setAgentState({})
      setDraft('')
      setActiveAssistantRun(null)
      setIsRunning(false)
      setPendingInterrupts([])
      setInterruptDrafts({})
      setSharedView(view)
      setIsLoadingThread(false)
    },
    [agent, agentEndpoint, aiServerBase, authenticatedFetch],
  )

  const abortRun = useCallback(() => {
    agent.abortRun()
    activeRunIdRef.current = null
    setActiveAssistantRun(null)
    setRunningState(false)
    setError('Run cancelled by user.')
  }, [agent, setRunningState])

  const setAllInterruptPayload = useCallback(
    (approved: boolean) => {
      setInterruptDrafts((current) =>
        setAllInterruptDrafts(current, pendingInterrupts, {
          status: 'resolved',
          payloadText: safeJson({ approved }),
        }),
      )
    },
    [pendingInterrupts],
  )

  const approveAllInterrupts = useCallback(() => {
    setAllInterruptPayload(true)
  }, [setAllInterruptPayload])

  const rejectAllInterrupts = useCallback(() => {
    setAllInterruptPayload(false)
  }, [setAllInterruptPayload])

  const cancelAllInterrupts = useCallback(() => {
    setInterruptDrafts((current) =>
      setAllInterruptDrafts(current, pendingInterrupts, {
        status: 'cancelled',
        payloadText: '',
      }),
    )
  }, [pendingInterrupts])

  const updateInterruptDraft = useCallback(
    (interruptId: string, nextDraft: InterruptDraft) => {
      setInterruptDrafts((current) => ({
        ...current,
        [interruptId]: nextDraft,
      }))
    },
    [],
  )

  const resumeInterrupts = useCallback(async () => {
    let resume: ResumeEntry[]

    try {
      resume = buildResumeEntries(pendingInterrupts, interruptDrafts)
    } catch (resumeError) {
      setError(
        resumeError instanceof Error
          ? resumeError.message
          : String(resumeError),
      )
      return
    }

    setPendingInterrupts([])
    setInterruptDrafts({})
    await runAgent(resume)
  }, [interruptDrafts, pendingInterrupts, runAgent])

  return {
    state: {
      agentEndpoint,
      mcpProxyEndpoint,
      threadId,
      messages,
      events,
      tools,
      agentState,
      draft,
      isRunning,
      isLoadingThread,
      activeAssistantRun,
      error,
      pendingInterrupts,
      interruptDrafts,
      canSend:
        draft.trim().length > 0 &&
        !isRunning &&
        !isForking &&
        !sharedView?.ownThreadId &&
        pendingInterrupts.length === 0,
      sharedView,
      sharedLinkError,
      isForking,
    },
    actions: {
      setAgentEndpoint,
      setMcpProxyEndpoint,
      setThreadId,
      setDraft,
      applyConnectionSettings,
      startNewThread,
      loadThread,
      openShared,
      submitDraft,
      submitQuestion,
      abortRun,
      approveAllInterrupts,
      rejectAllInterrupts,
      cancelAllInterrupts,
      updateInterruptDraft,
      resumeInterrupts,
    },
  }
}
